import React, { useState }                      from "react";
import { graphql }                              from "gatsby";
import Img                                      from "gatsby-image";
import { useTheme }                             from "@chakra-ui/react";
import { FaChevronDown, FaChevronUp }           from "react-icons/fa";
import { GiBigDiamondRing }                     from "react-icons/gi";
import { Container, Row, Col }                  from "react-grid-system";
import Breakpoint, { useCurrentBreakpointName } from "react-socks";
import AnimateHeight                            from "react-animate-height";
import styled                                   from "@emotion/styled";
import { readableColor }                        from "polished";


import SEO          from "@interness/web-core/src/components/modules/SEO/SEO";
import Spacer       from "@interness/web-core/src/components/structure/Spacer/Spacer";
import Wrapper      from "@interness/web-core/src/components/structure/Wrapper/Wrapper";
import Logo         from "@interness/web-core/src/components/media/Logo/Logo";
import FramedImage  from "@interness/theme-sonora/src/components/FramedImage";
import Carousel     from "@interness/web-core/src/components/media/Carousel/Carousel";
import CallToAction from "@interness/theme-sonora/src/components/CallToAction/CallToAction";
import HeaderImage  from "@interness/web-core/src/components/media/HeaderImage/HeaderImage";
import Heading      from "@interness/web-core/src/components/text/Heading/Heading";

const TextCol = styled.div`
  text-align: justify;
`;

const BtnContainer = styled.div`
  text-align: center;
  margin: 20px 0;

  button {
    color: ${props => readableColor(props.theme.base_color)};
  }

  svg {
    vertical-align: text-top;
    margin-left: 10px;
  }
`;

const TrauringePage = ({ data }) => {
  const theme = useTheme();

  const breakpoint = useCurrentBreakpointName();
  const [showLongDescrOnMobile, toggleDescr] = useState(false);
  const toggleMore = () => {
    toggleDescr(!showLongDescrOnMobile);
  };

  return (
    <>
      <SEO title="Trauringe" />
      <HeaderImage>
        <Img fluid={data.trauringeHeader.images[0].file.localFile.childImageSharp.fluid}></Img>
      </HeaderImage>
      <Spacer />
      <Heading subtitle="Lebenstraumringe – zeitlose Symbole der Liebe" tag={"h2"}
               icon={<GiBigDiamondRing />}>Trauringe</Heading>
      <Spacer />
      <Wrapper>
        <Container fluid>
          <Row>
            <Col md={6}>
              <div style={{ textAlign: "center" }}>
                <Logo logo={data.directusProject.logo.localFile} title="Trauringe Meckenstock" svgWidth="240px"
                      svgHeight="80px" />
              </div>
            </Col>
            <Col md={6} />
          </Row>
          <Row>
            <Col md={6}>
              <Breakpoint large down>
                <AnimateHeight height={!showLongDescrOnMobile ? "auto" : 0}>
                  <div style={{ margin: "20px 0 0 0", textAlign: "justify" }}>
                    <p>Mit einem Verlobungsring beginnt ein neuer Abschnitt im Leben zweier Menschen. Er steht für das
                      Versprechen...</p>
                  </div>
                </AnimateHeight>
                <BtnContainer>
                  <button onClick={toggleMore}>
                    Mehr über Trauringe
                    {showLongDescrOnMobile ? <FaChevronUp /> : <FaChevronDown />}
                  </button>
                </BtnContainer>
              </Breakpoint>
              <AnimateHeight height={breakpoint === "xlarge" || showLongDescrOnMobile ? "auto" : 0}>
                <TextCol>
                  <p>Mit einem Verlobungsring beginnt ein neuer Abschnitt im Leben zweier Menschen. Er steht für das
                    Versprechen, den gemeinsamen Weg zu gehen, und für die Gewissheit, zusammenzuhalten – heute,
                    morgen, für immer. Mit der Hochzeit wird dieses Versprechen besiegelt: Der Ehering wird zum
                    wertvollen Begleiter und zum Sinnbild einer Liebe, die dauerhaft Bestand hat.</p>
                  <p>Ein Trauring ist mehr als Schmuck. Er ist ein Symbol der Zuneigung und erinnert Tag für Tag an
                    den einzigartigen Moment, an dem zwei Herzen „Ja” zueinander gesagt haben. Wer sich bindet,
                    vertraut seinem Partner das Kostbarste an – sich selbst.</p>
                  <p>Unsere Kollektionen spiegeln diese besondere Bedeutung wider. Klare Linien, raffinierte Details
                    und feinste Inspirationen verleihen jedem Ring seine unverwechselbare Eleganz. Höchste Qualität,
                    vollendete Schmiedekunst und ein außergewöhnlicher Tragekomfort machen unsere Ringe zu
                    Begleitern fürs Leben.</p>
                  <p>Wir verwenden nur ausschließlich Materialien aus nachhaltigen und fairen Quellen. 100% Recycling
                    Gold und fair gehandeltes Gold. Nachhaltige Eheringe anzubieten ist für uns eine besondere
                    Herzensangelegenheit.</p>
                </TextCol>
              </AnimateHeight>
            </Col>
            <Col md={6}>
              <FramedImage borderOnGatsbyImage sx={{
                "img, figure": {
                  margin: 0
                },
                ".int-custom .slick-dots li button": {
                  backgroundColor: theme.colorMode === "light" ? "#333" : "#fff"
                },
                ".int-custom .slick-dots li.slick-active button": {
                  backgroundColor: theme.brand_color
                }
              }}>
                <Carousel>
                  {data.trauringeGalerie.images.map((image, i) => {
                    image = image.file;
                    return (
                      <figure key={image.localFile.name}>
                        <Img fluid={image.localFile.childImageSharp.fluid} alt={image.localFile.name}
                             loading={i === 0 ? "eager" : "lazy"} />
                        {image.description &&
                          <figcaption dangerouslySetInnerHTML={{ __html: image.description }} />}
                      </figure>
                    );
                  })}
                </Carousel>
              </FramedImage>
            </Col>
          </Row>
          <Spacer />
        </Container>
        <CallToAction />
      </Wrapper>
    </>
  );
};

export default TrauringePage;

export const query = graphql`
  query {
    directusProject {
      logo {
        localFile {
          name
          extension
          publicURL
          childImageSharp {
            fluid(maxHeight: 80, quality: 100) {
              ...GatsbyImageSharpFluid
            }
          }
        }
      }
    }
    trauringeHeader: directusMediaCollection(name: {eq: "trauringe-header"}) {
      name
      images: media {
        file {
          localFile {
            name
            childImageSharp {
              fluid(maxWidth: 1600, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
    trauringeGalerie: directusMediaCollection(name: {eq: "trauringe-galerie"}) {
      name
      images: media {
        file {
          localFile {
            name
            childImageSharp {
              fluid(maxWidth: 800, cropFocus: CENTER) {
                ...GatsbyImageSharpFluid
              }
            }
          }
        }
      }
    }
  }
`;